import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import router from "@/router";
import swal from "sweetalert2";
import to from "await-to-js";
import { RequestBuilder, RequestOptions } from "@/services/RequestBuilder";
import { ApiResult } from "@/services/ApiResult";

const config: AxiosRequestConfig = {
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  baseURL: window.location.origin,
};

export abstract class BaseApi {
  private api: AxiosInstance;
  private opts: RequestOptions;

  constructor() {
    this.api = axios.create(config);
    this.opts = new RequestOptions();
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request to API", error);
          return new Promise(function (resolve) {
            swal.fire("Unauthorized", "", "warning").then(() => {
              if (router.currentRoute.value.name != "login")
                window.location.assign("");
              resolve({ errorMessage: "Unauthorized" });
            });
          });
        }
        if (error.response && error.response.status === 403) {
          console.log("No permission request to API", error);
          return {
            data: {
              error:
                /*i18n.t('general.permission.accessdenied')*/ "No permission",
            },
          };
        }
        if (!error.response && error.isAxiosError && error.message) {
          console.log("NetworkError", error.message);
          return {
            data: {
              error:
                /*i18n.t('general.permission.accessdenied')*/ error.message,
            },
          };
        }
        return Promise.reject(error);
      }
    );
  }

  public withLoader(): this {
    this.opts.withLoader = true;
    return this;
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> {
    const req = async () => {
      const [axiosError, axiosResponse] = await to(this.api.get(url, config));
      return new ApiResult(axiosError, axiosResponse);
    };
    return await this.buildRequest(req)();
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> {
    const req = async () => {
      const [axiosError, axiosResponse] = await to(
        this.api.delete(url, config)
      );
      return new ApiResult(axiosError, axiosResponse);
    };
    return await this.buildRequest(req)();
  }

  public async post<T>(
    url: string,
    data?: string | object,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> {
    const req = async () => {
      const [axiosError, axiosResponse] = await to(
        this.api.post(url, data, config)
      );
      return new ApiResult(axiosError, axiosResponse);
    };
    return await this.buildRequest(req)();
  }

  public async postFormData<T>(
    url: string,
    data: FormData,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> {
    const req = async () => {
      const [axiosError, axiosResponse] = await to(
        this.api.post(url, data, config)
      );
      return new ApiResult(axiosError, axiosResponse);
    };
    return await this.buildRequest(req)();
  }

  public async put<T>(
    url: string,
    data?: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> {
    const req = async () => {
      const [axiosError, axiosResponse] = await to(
        this.api.put(url, data, config)
      );
      return new ApiResult(axiosError, axiosResponse);
    };
    return await this.buildRequest(req)();
  }

  private buildRequest<T>(
    req: () => Promise<ApiResult<T>>
  ): () => Promise<ApiResult<T>> {
    const builder = new RequestBuilder<T>(req, this.opts);
    this.opts = new RequestOptions();
    return builder.build();
  }
}
